/**
 * Classification of the backend's refusal vocabulary.
 *
 * OMNΛS layers availability, and each layer refuses differently. Collapsing
 * them into one "unavailable" would tell a PM to call support when the real
 * answer is "your organization has not been granted this yet" — or the
 * reverse. The layers, outermost first:
 *
 *   GLOBAL FLAG      503 {code: not_enabled}          → 'disabled'
 *   TENANT ENTITLEMENT 403 {code: tenant_not_entitled} → 'not-entitled'
 *   USER CAPABILITY  403 {code: capability_required}   → 'no-access'
 *   USER ROLE        403 "Insufficient permissions"    → 'no-access'
 *
 * Note the detail shape is NOT consistent across statuses: 403/503 carry an
 * object {code, message}; role refusals and 404s carry a plain string. Both
 * are handled here so no caller has to know.
 *
 * Dependency-free on purpose — this is the module the test harness drives.
 */

/**
 * `detail` carries the raw object detail through when there is one, because
 * some refusals are structured answers rather than just a message: an identity
 * review names its candidate records, a duplicate lineage names both bidder
 * keys. A caller that needs those must not have to re-parse the axios error.
 *
 * @returns {{kind: string, message: string, code: string|null, status: number|null, detail: object|null}}
 *
 * kinds:
 *  'disabled'      — platform flag off (503 not_enabled)
 *  'not-entitled'  — tenant not granted this surface (403 tenant_not_entitled)
 *  'no-access'     — this user's role/capability may not (403)
 *  'not-found'     — 404
 *  'conflict'      — 409 (no_quotes, already_decided, …)
 *  'stale'         — 409 stale_proposal / stale_award (facts moved)
 *  'confirmation'  — 428, confirmed:true was required
 *  'invalid'       — 400/422 validation refusal
 *  'network'       — no response at all
 *  'server'        — anything else
 */
export const classifyWorkflowError = (error) => {
  if (!error?.response) {
    return {
      kind: 'network',
      message: 'Could not reach the server. Check your connection and retry.',
      code: null,
      status: null,
      detail: null,
    };
  }
  const { status, data } = error.response;
  const detail = data?.detail;
  const isObjectDetail = typeof detail === 'object' && detail !== null && !Array.isArray(detail);
  const code = isObjectDetail ? detail.code || null : null;
  const message =
    (isObjectDetail && detail.message) ||
    (typeof detail === 'string' && detail) ||
    error.message ||
    'Request failed.';

  const carried = isObjectDetail ? detail : null;

  if (status === 503 && code === 'not_enabled') {
    return { kind: 'disabled', message, code, status, detail: carried };
  }
  if (status === 403) {
    // A tenant entitlement refusal is not the same as a permission refusal:
    // no role change fixes it, and the user is not doing anything wrong.
    if (code === 'tenant_not_entitled' || code === 'unknown_entitlement') {
      return { kind: 'not-entitled', message, code, status, detail: carried };
    }
    return { kind: 'no-access', message, code, status, detail: carried };
  }
  if (status === 404) {
    return { kind: 'not-found', message, code, status, detail: carried };
  }
  if (status === 409) {
    // stale_proposal (decisions) and stale_award (execution authorization)
    // both mean "the facts moved since this was calculated".
    return {
      kind: code === 'stale_proposal' || code === 'stale_award' ? 'stale' : 'conflict',
      message,
      code,
      status,
      detail: carried,
    };
  }
  if (status === 428) {
    return { kind: 'confirmation', message, code, status, detail: carried };
  }
  if (status === 400 || status === 422) {
    return { kind: 'invalid', message, code, status, detail: carried };
  }
  return { kind: 'server', message, code, status, detail: carried };
};

export default classifyWorkflowError;
